import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import AppLogo from '../../components/AppLogo';
import Icon from '../../components/AppIcon';

const LegalPageLayout = ({ title, description, sections = [], children }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(sections[0]?.id || '');

  const content = useMemo(() => React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return child;
    const sectionId = child.props.id || sections[index]?.id;
    return React.cloneElement(child, {
      id: sectionId,
      className: `${child.props.className || ''} scroll-mt-28`.trim(),
    });
  }), [children, sections]);

  useEffect(() => {
    if (!sections.length || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver((entries) => {
      const visibleSection = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (visibleSection?.target?.id) setActiveSection(visibleSection.target.id);
    }, { rootMargin: '-15% 0px -65% 0px', threshold: 0 });

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [sections]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/discover');
  };

  return (
  <div className="min-h-screen bg-background text-foreground">
    <Helmet>
      <title>{title} | Clawappz</title>
      <meta name="description" content={description} />
    </Helmet>

    <header className="border-b border-border bg-card">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <Link to="/discover" className="inline-flex items-center" aria-label="Clawappz home">
          <AppLogo variant="wordmark" size={116} color="var(--color-primary)" shape="none" />
        </Link>
        <button type="button" onClick={handleBack} className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground font-caption">
          <Icon name="ArrowLeft" size={16} />
          Back
        </button>
      </div>
    </header>

    <main className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-10 px-4 py-10 sm:px-6 sm:py-14 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-14 lg:px-8">
      <aside className="hidden lg:block">
        <nav className="sticky top-8 border-l border-border pl-5" aria-label={`${title} sections`}>
          <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground font-caption">On this page</p>
          <ol className="space-y-1">
            {sections.map((section, index) => {
              const isActive = activeSection === section.id;
              return (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    onClick={() => setActiveSection(section.id)}
                    aria-current={isActive ? 'location' : undefined}
                    className={`block border-l-2 py-2 pl-4 text-sm leading-5 transition-colors font-caption ${
                      isActive
                        ? '-ml-[22px] border-primary font-semibold text-primary'
                        : '-ml-[22px] border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {index + 1}. {section.label}
                  </a>
                </li>
              );
            })}
          </ol>
        </nav>
      </aside>

      <div className="min-w-0">
        <div className="mb-8 border-b border-border pb-8 sm:mb-10">
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-muted-foreground font-caption">{description}</p>
          <p className="mt-4 text-sm text-muted-foreground font-caption">Last updated: September 4, 2026</p>
        </div>

        <details className="mb-9 border-y border-border py-4 lg:hidden">
          <summary className="cursor-pointer text-sm font-semibold text-foreground font-caption">On this page</summary>
          <ol className="mt-4 grid gap-1 sm:grid-cols-2">
            {sections.map((section, index) => (
              <li key={section.id}>
                <a href={`#${section.id}`} className="block py-2 text-sm text-muted-foreground transition-colors hover:text-primary font-caption">
                  {index + 1}. {section.label}
                </a>
              </li>
            ))}
          </ol>
        </details>

        <article className="space-y-10 font-caption [&_h2]:mb-3 [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-semibold [&_li]:leading-7 [&_p]:leading-7 [&_p]:text-muted-foreground [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6">
          {content}
        </article>
      </div>
    </main>

    <footer className="border-t border-border bg-card">
      <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-3 px-4 py-6 text-sm text-muted-foreground sm:flex-row sm:px-6 lg:px-8 font-caption">
        <span>© {new Date().getFullYear()} Clawappz.com</span>
        <nav className="flex items-center gap-5" aria-label="Legal">
          <Link to="/terms-of-service" className="transition-colors hover:text-foreground">Terms of Service</Link>
          <Link to="/privacy-policy" className="transition-colors hover:text-foreground">Privacy Policy</Link>
        </nav>
      </div>
    </footer>
  </div>
  );
};

export default LegalPageLayout;
